import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  Users, 
  Briefcase, 
  ArrowRight,
  Calendar
} from 'lucide-react';
import { usePartnerAuth } from '@/hooks/usePartnerAuth';
import { supabase } from '@/integrations/supabase/client';
import { format, isToday, isTomorrow, startOfWeek, endOfWeek, subWeeks, startOfMonth, endOfMonth, startOfYear, endOfYear, addDays, parseISO } from 'date-fns';

// Helper function to build service description based on offerings
const getServiceDescription = (partner: { offers_flowers: boolean; offers_cleaning: boolean } | null): string => {
  if (!partner) return 'service requests';

  const services: string[] = [];
  if (partner.offers_flowers) services.push('flower deliveries');
  if (partner.offers_cleaning) services.push('cleaning requests');

  if (services.length === 0) return 'service requests';
  if (services.length === 1) return services[0];
  return `${services[0]} and ${services[1]}`;
};

interface EarningsData {
  totalEarnings: number;
  weeklyEarnings: number;
  monthlyEarnings: number;
  yearlyEarnings: number;
}

interface UrgentOrders {
  dueToday: number;
  dueTomorrow: number;
}

interface PerformanceData {
  thisWeekDeliveries: number;
  lastWeekDeliveries: number;
  percentageChange: number;
}

interface ScheduleDay {
  date: Date;
  label: string;
  count: number;
}

const PartnerDashboard = () => {
  const navigate = useNavigate();
  const { partner, isOwner, hasPermission } = usePartnerAuth();
  const [earnings, setEarnings] = useState<EarningsData>({ totalEarnings: 0, weeklyEarnings: 0, monthlyEarnings: 0, yearlyEarnings: 0 });
  const [earningsPeriod, setEarningsPeriod] = useState<string>('week');
  const [urgentOrders, setUrgentOrders] = useState<UrgentOrders>({ dueToday: 0, dueTomorrow: 0 });
  const [performance, setPerformance] = useState<PerformanceData>({ thisWeekDeliveries: 0, lastWeekDeliveries: 0, percentageChange: 0 });
  const [schedule, setSchedule] = useState<ScheduleDay[]>([]);
  const [teamCount, setTeamCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const canViewEarnings = hasPermission('view_earnings');

  const fetchDashboardData = useCallback(async () => {
    if (!partner?.id) return;

    try {
      const now = new Date();
      const thisWeekStart = startOfWeek(now, { weekStartsOn: 1 });
      const thisWeekEnd = endOfWeek(now, { weekStartsOn: 1 });
      const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
      const lastWeekEnd = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
      const monthStart = startOfMonth(now);
      const monthEnd = endOfMonth(now);
      const yearStart = startOfYear(now);
      const yearEnd = endOfYear(now);

      // Fetch all orders for this partner
      const { data: orders, error: ordersError } = await supabase
        .from('orders')
        .select('id, status, delivery_date, partner_payout_cents, delivered_at')
        .eq('fulfillment_partner_id', partner.id);

      if (ordersError) {
        console.error('Error fetching orders:', ordersError);
        return;
      }

      if (orders) {
        // Only calculate earnings if user has permission
        if (canViewEarnings) {
          // Fetch fulfillment payout records for this partner only.
          // Commission records belong in the Monument Portal, not here.
          const { data: payoutRecords, error: payoutError } = await supabase
            .from('payout_records')
            .select('payout_amount_cents, delivered_at, payout_status, orders(service_type)')
            .eq('partner_id', partner.id)
            .eq('payout_status', 'paid');

          if (!payoutError && payoutRecords) {
            let totalEarnings = 0;
            let weeklyEarnings = 0;
            let monthlyEarnings = 0;
            let yearlyEarnings = 0;

            payoutRecords.forEach(p => {
              // Filter: only count payouts for service types this partner offers
              const order = p.orders as { service_type: string } | null;
              const serviceType = order?.service_type?.toLowerCase() || '';
              if (serviceType === 'flowers' && !partner.offers_flowers) return;
              if (serviceType === 'cleaning' && !partner.offers_cleaning) return;

              const amount = (p.payout_amount_cents || 0) / 100;
              const deliveredDate = p.delivered_at ? new Date(p.delivered_at) : null;

              totalEarnings += amount;

              if (deliveredDate && deliveredDate >= thisWeekStart && deliveredDate <= thisWeekEnd) {
                weeklyEarnings += amount;
              }
              if (deliveredDate && deliveredDate >= monthStart && deliveredDate <= monthEnd) {
                monthlyEarnings += amount;
              }
              if (deliveredDate && deliveredDate >= yearStart && deliveredDate <= yearEnd) {
                yearlyEarnings += amount;
              }
            });

            setEarnings({ totalEarnings, weeklyEarnings, monthlyEarnings, yearlyEarnings });
          } else if (payoutError) {
            console.error('Error fetching payout records:', payoutError);
          }
        }

        // Calculate urgent orders (active orders due today/tomorrow)
        const activeOrders = orders.filter(o => 
          o.status === 'assigned' || o.status === 'accepted' || o.status === 'in_progress'
        );
        const dueToday = activeOrders.filter(o => isToday(parseISO(o.delivery_date))).length;
        const dueTomorrow = activeOrders.filter(o => isTomorrow(parseISO(o.delivery_date))).length;
        setUrgentOrders({ dueToday, dueTomorrow });

        // Calculate performance (deliveries this week vs last week)
        const deliveredOrders = orders.filter(o => o.status === 'delivered');
        const thisWeekDeliveries = deliveredOrders.filter(o => {
          const deliveredDate = o.delivered_at ? new Date(o.delivered_at) : null;
          return deliveredDate && deliveredDate >= thisWeekStart && deliveredDate <= thisWeekEnd;
        }).length;

        const lastWeekDeliveries = deliveredOrders.filter(o => {
          const deliveredDate = o.delivered_at ? new Date(o.delivered_at) : null;
          return deliveredDate && deliveredDate >= lastWeekStart && deliveredDate <= lastWeekEnd;
        }).length;

        const percentageChange = lastWeekDeliveries > 0 
          ? ((thisWeekDeliveries - lastWeekDeliveries) / lastWeekDeliveries) * 100 
          : thisWeekDeliveries > 0 ? 100 : 0;

        setPerformance({ thisWeekDeliveries, lastWeekDeliveries, percentageChange });

        // Calculate upcoming schedule (next 7 days)
        const upcomingSchedule: ScheduleDay[] = [];
        for (let i = 0; i < 7; i++) {
          const date = addDays(now, i);
          const dayOrders = activeOrders.filter(o => {
            const deliveryDate = parseISO(o.delivery_date);
            return format(deliveryDate, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd');
          });
          
          let label = format(date, 'EEE, MMM d');
          if (i === 0) label = `Today (${format(date, 'MMM d')})`;
          if (i === 1) label = `Tomorrow (${format(date, 'MMM d')})`;
          
          upcomingSchedule.push({ date, label, count: dayOrders.length });
        }
        setSchedule(upcomingSchedule);
      }

      // Fetch team count if owner
      if (isOwner) {
        const { count, error } = await supabase
          .from('fulfillment_partner_users')
          .select('*', { count: 'exact', head: true })
          .eq('partner_id', partner.id);

        if (!error && count !== null) {
          setTeamCount(count);
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [partner?.id, partner?.offers_flowers, partner?.offers_cleaning, isOwner, canViewEarnings]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Real-time subscription for order updates
  useEffect(() => {
    if (!partner?.id) return;

    const channel = supabase
      .channel('partner-dashboard-orders')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: `fulfillment_partner_id=eq.${partner.id}`
        },
        () => {
          fetchDashboardData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [partner?.id, fetchDashboardData]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-96" />
        </div>
        <Skeleton className="h-20 w-full" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </div>
    );
  }

  const hasUrgentOrders = urgentOrders.dueToday > 0 || urgentOrders.dueTomorrow > 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground">
          Welcome back, {partner?.name || 'Partner'}
        </p>
      </div>

      {/* Urgent Action Items */}
      {hasUrgentOrders && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-amber-500/10">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-amber-900 dark:text-amber-100">
                  {urgentOrders.dueToday > 0 && (
                    <span>{urgentOrders.dueToday} order{urgentOrders.dueToday !== 1 ? 's' : ''} due today</span>
                  )}
                  {urgentOrders.dueToday > 0 && urgentOrders.dueTomorrow > 0 && ', '}
                  {urgentOrders.dueTomorrow > 0 && (
                    <span>{urgentOrders.dueTomorrow} due tomorrow</span>
                  )}
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  These orders need your attention
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="border-amber-500/50 hover:bg-amber-500/10"
              onClick={() => navigate('/partner/orders')}
            >
              View Orders
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Earnings Summary Card - Only show if user has permission */}
      {canViewEarnings && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Earnings</CardTitle>
            <Select value={earningsPeriod} onValueChange={setEarningsPeriod}>
              <SelectTrigger className="w-[140px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="year">This Year</SelectItem>
                <SelectItem value="total">All Time</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${(earningsPeriod === 'week' ? earnings.weeklyEarnings :
                 earningsPeriod === 'month' ? earnings.monthlyEarnings :
                 earningsPeriod === 'year' ? earnings.yearlyEarnings :
                 earnings.totalEarnings).toFixed(2)}
            </div>
            <Button 
              variant="link" 
              className="px-0 text-xs h-auto" 
              onClick={() => navigate('/partner/earnings')}
            >
              View earnings details →
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Performance & Schedule Row */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Performance Snapshot */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Performance</CardTitle>
            <CardDescription>Week-over-week comparison</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">This week</p>
                <p className="text-2xl font-bold">{performance.thisWeekDeliveries} deliveries</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Last week</p>
                <p className="text-2xl font-bold">{performance.lastWeekDeliveries} deliveries</p>
              </div>
            </div>
            
            {(performance.thisWeekDeliveries > 0 || performance.lastWeekDeliveries > 0) && (
              <div className={`flex items-center gap-2 p-3 rounded-lg ${
                performance.percentageChange >= 0 
                  ? 'bg-green-500/10 text-green-700 dark:text-green-400' 
                  : 'bg-red-500/10 text-red-700 dark:text-red-400'
              }`}>
                {performance.percentageChange >= 0 ? (
                  <TrendingUp className="h-4 w-4" />
                ) : (
                  <TrendingDown className="h-4 w-4" />
                )}
                <span className="font-medium">
                  {performance.percentageChange >= 0 ? '+' : ''}{performance.percentageChange.toFixed(0)}% 
                  {performance.percentageChange >= 0 ? ' improvement' : ' change'}
                </span>
              </div>
            )}

            {isOwner && (
              <div className="flex items-center gap-2 pt-2 border-t">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Team: {teamCount} member{teamCount !== 1 ? 's' : ''}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Schedule */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Upcoming Schedule</CardTitle>
            <CardDescription>Next 7 days</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {schedule.map((day) => (
                <div
                  key={day.label}
                  className={`flex items-center justify-between p-2 rounded-lg transition-colors ${
                    day.count > 0 
                      ? 'bg-primary/5 hover:bg-primary/10 cursor-pointer' 
                      : 'text-muted-foreground'
                  }`}
                  onClick={() => day.count > 0 && navigate('/partner/orders')}
                >
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span className="text-sm">{day.label}</span>
                  </div>
                  <span className={`text-sm font-medium ${day.count > 0 ? '' : 'text-muted-foreground'}`}>
                    {day.count > 0 ? `${day.count} delivery${day.count !== 1 ? 'ies' : ''}` : '—'}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Welcome Card */}
      <Card className="bg-primary/5 border-primary/20">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-primary/10">
              <Briefcase className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Welcome to the Partner Portal</CardTitle>
              <CardDescription>
                You're all set up to start receiving {getServiceDescription(partner)}.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Orders will appear here when customers place requests for gravesites in your service area. 
            You'll be notified of new orders and can manage all your services from this portal.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PartnerDashboard;
